import React, { useState, useEffect, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { createTicket, getCategories } from "./supportClient";
import { ErrorBanner, FieldError, useFormErrors } from "./FormError";
import { useI18n } from "./i18n";

/* ─── Translations ────────────────────────────────────────────────────────── */
const TM = {
  title:     { fr:"Contacter le",          en:"Contact",            es:"Contactar al",         de:"Support",               pt:"Contatar o",           ar:"تواصل مع"        },
  title2:    { fr:"Support",               en:"Support",            es:"Soporte",              de:"kontaktieren",          pt:"Suporte",              ar:"الدعم"           },
  sub:       { fr:"Notre équipe vous répond généralement sous 24h.", en:"Our team usually responds within 24h.", es:"Nuestro equipo responde en 24h.", de:"Unser Team antwortet in der Regel innerhalb von 24h.", pt:"Nossa equipe responde em 24h.", ar:"يرد فريقنا عادةً خلال 24 ساعة." },
  subjectL:  { fr:"Sujet *",               en:"Subject *",          es:"Asunto *",             de:"Betreff *",             pt:"Assunto *",            ar:"الموضوع *"       },
  subjectPh: { fr:"Résumez votre problème en une phrase…", en:"Summarize your issue in one sentence…", es:"Resume tu problema en una oración…", de:"Problem in einem Satz…", pt:"Resuma seu problema em uma frase…", ar:"لخّص مشكلتك في جملة…" },
  catL:      { fr:"Catégorie *",           en:"Category *",         es:"Categoría *",          de:"Kategorie *",           pt:"Categoria *",          ar:"الفئة *"         },
  prioL:     { fr:"Priorité",              en:"Priority",           es:"Prioridad",            de:"Priorität",             pt:"Prioridade",           ar:"الأولوية"        },
  descL:     { fr:"Description *",         en:"Description *",      es:"Descripción *",        de:"Beschreibung *",        pt:"Descrição *",          ar:"الوصف *"         },
  descPh:    { fr:"Décrivez votre problème en détail. Plus vous donnez d'informations, plus vite nous pourrons vous aider.",
               en:"Describe your issue in detail. The more information you give, the faster we can help you.",
               es:"Describe tu problema en detalle. Cuanta más información des, antes podremos ayudarte.",
               de:"Beschreiben Sie Ihr Problem im Detail. Je mehr Informationen Sie geben, desto schneller können wir Ihnen helfen.",
               pt:"Descreva seu problema em detalhe. Quanto mais informações você der, mais rápido poderemos ajudá-lo.",
               ar:"اشرح مشكلتك بالتفصيل. كلما قدمت معلومات أكثر، كلما تمكّنا من مساعدتك بسرعة." },
  cancel:    { fr:"Annuler",               en:"Cancel",             es:"Cancelar",             de:"Abbrechen",             pt:"Cancelar",             ar:"إلغاء"           },
  send:      { fr:"Envoyer le ticket →",   en:"Send ticket →",      es:"Enviar ticket →",      de:"Ticket senden →",       pt:"Enviar ticket →",      ar:"إرسال التذكرة →" },
  sending:   { fr:"Envoi en cours…",       en:"Sending…",           es:"Enviando…",            de:"Senden…",               pt:"Enviando…",            ar:"جارٍ الإرسال…"   },
  success:   { fr:"Ticket envoyé !",       en:"Ticket sent!",       es:"¡Ticket enviado!",     de:"Ticket gesendet!",      pt:"Ticket enviado!",      ar:"تم إرسال التذكرة!" },
  successSub:{ fr:"Notre équipe support va prendre en charge votre demande dans les plus brefs délais.",
               en:"Our support team will handle your request as soon as possible.",
               es:"Nuestro equipo atenderá tu solicitud pronto.",
               de:"Unser Support-Team bearbeitet Ihre Anfrage in Kürze.",
               pt:"Nossa equipe tratará sua solicitação em breve.",
               ar:"سيتولى فريق الدعم لدينا طلبك في أقرب وقت ممكن." },
  close:     { fr:"Fermer",                en:"Close",              es:"Cerrar",               de:"Schließen",             pt:"Fechar",               ar:"إغلاق"           },
  ref:       { fr:"Réf.",                  en:"Ref.",               es:"Ref.",                 de:"Ref.",                  pt:"Ref.",                 ar:"المرجع"          },

  /* Priority options */
  low:       { fr:"Basse",                 en:"Low",                es:"Baja",                 de:"Niedrig",               pt:"Baixa",                ar:"منخفضة"          },
  normal:    { fr:"Normale",               en:"Normal",             es:"Normal",               de:"Normal",                pt:"Normal",               ar:"عادية"           },
  high:      { fr:"Haute",                 en:"High",               es:"Alta",                 de:"Hoch",                  pt:"Alta",                 ar:"عالية"           },
  urgent:    { fr:"Urgente",               en:"Urgent",             es:"Urgente",              de:"Dringend",              pt:"Urgente",              ar:"عاجلة"           },

  /* Fallback technical categories (used when API doesn't return labels) */
  "cat.deposit":    { fr:"Dépôt",          en:"Deposit",            es:"Depósito",             de:"Einzahlung",            pt:"Depósito",             ar:"إيداع"           },
  "cat.withdrawal": { fr:"Retrait",        en:"Withdrawal",         es:"Retiro",               de:"Auszahlung",            pt:"Retirada",             ar:"سحب"             },
  "cat.trade":      { fr:"Trade",          en:"Trade",              es:"Trade",                de:"Handel",                pt:"Trade",                ar:"تداول"           },
  "cat.account":    { fr:"Compte",         en:"Account",            es:"Cuenta",               de:"Konto",                 pt:"Conta",                ar:"الحساب"          },
  "cat.bug":        { fr:"Bug",            en:"Bug",                es:"Error",                de:"Fehler",                pt:"Bug",                  ar:"خلل"             },
  "cat.other":      { fr:"Autre",          en:"Other",              es:"Otro",                 de:"Sonstiges",             pt:"Outro",                ar:"أخرى"            },

  /* Info box */
  "info.logged.1":  { fr:"Connecté en tant que",                    en:"Signed in as",                       es:"Conectado como",                     de:"Angemeldet als",                       pt:"Conectado como",                       ar:"مسجّل الدخول باسم" },
  "info.logged.2":  { fr:". Votre ticket sera lié à votre compte.", en:". Your ticket will be linked to your account.", es:". Tu ticket se vinculará a tu cuenta.", de:". Ihr Ticket wird mit Ihrem Konto verknüpft.", pt:". Seu ticket será vinculado à sua conta.", ar:". سيتم ربط تذكرتك بحسابك." },
  "info.guest":     { fr:"Connectez-vous pour suivre l'avancement de votre ticket dans votre espace personnel.",
                      en:"Sign in to track your ticket progress in your personal space.",
                      es:"Inicia sesión para seguir el progreso de tu ticket en tu espacio personal.",
                      de:"Melden Sie sich an, um den Fortschritt Ihres Tickets in Ihrem persönlichen Bereich zu verfolgen.",
                      pt:"Faça login para acompanhar o progresso do seu ticket no seu espaço pessoal.",
                      ar:"سجّل الدخول لمتابعة تقدم تذكرتك في مساحتك الشخصية." },

  /* Validation errors */
  "err.subject":    { fr:"Le sujet est requis.",       en:"Subject is required.",      es:"El asunto es obligatorio.",  de:"Betreff ist erforderlich.",   pt:"O assunto é obrigatório.",   ar:"الموضوع مطلوب." },
  "err.desc":       { fr:"Minimum 10 caractères.",     en:"Minimum 10 characters.",    es:"Mínimo 10 caracteres.",      de:"Mindestens 10 Zeichen.",      pt:"Mínimo 10 caracteres.",      ar:"10 أحرف على الأقل." },

  /* Business categories (unused dropdown, kept from original) */
  cats: {
    fr:[["support","🎫 Support EVP"],["rewards","🏆 Récompenses & EVP"],["legal","⚖ Légal / RGPD"],["press","📰 Presse"],["partnership","🤝 Partenariat"],["other","💬 Autre"]],
    en:[["support","🎫 EVP Support"],["rewards","🏆 Rewards & EVP"],["legal","⚖ Legal / GDPR"],["press","📰 Press"],["partnership","🤝 Partnership"],["other","💬 Other"]],
    es:[["support","🎫 Soporte EVP"],["rewards","🏆 Recompensas & EVP"],["legal","⚖ Legal / RGPD"],["press","📰 Prensa"],["partnership","🤝 Alianza"],["other","💬 Otro"]],
    de:[["support","🎫 EVP-Support"],["rewards","🏆 Belohnungen & EVP"],["legal","⚖ Legal / DSGVO"],["press","📰 Presse"],["partnership","🤝 Partnerschaft"],["other","💬 Sonstiges"]],
    pt:[["support","🎫 Suporte EVP"],["rewards","🏆 Recompensas & EVP"],["legal","⚖ Legal / LGPD"],["press","📰 Imprensa"],["partnership","🤝 Parceria"],["other","💬 Outro"]],
    ar:[["support","🎫 دعم EVP"],["rewards","🏆 مكافآت & EVP"],["legal","⚖ قانوني"],["press","📰 صحافة"],["partnership","🤝 شراكة"],["other","💬 أخرى"]],
  },
};
function tm(key, lang) { return TM[key]?.[lang] ?? TM[key]?.fr ?? key; }

/* ─── Animations & styles ─────────────────────────────────────────────────── */
const fadeIn  = keyframes`from{opacity:0}to{opacity:1}`;
const slideUp = keyframes`from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}`;

const Overlay = styled.div`
  position:fixed;inset:0;z-index:400;
  background:rgba(0,0,0,.82);backdrop-filter:blur(10px);
  display:flex;align-items:center;justify-content:center;padding:1rem;
  animation:${fadeIn} .15s ease;
`;
const Modal = styled.div`
  background:${({theme})=>theme.bgCard};
  border:1px solid ${({theme})=>theme.borderLight};
  border-radius:18px;width:100%;max-width:520px;
  overflow:hidden;animation:${slideUp} .2s ease;
`;
const Head = styled.div`
  padding:1.25rem 1.5rem;border-bottom:1px solid ${({theme})=>theme.border};
  display:flex;align-items:flex-start;justify-content:space-between;gap:1rem;
`;
const HeadLeft = styled.div``;
const Title = styled.h2`
  font-family:${({theme})=>theme.fontDisplay};
  font-size:1.15rem;font-weight:700;letter-spacing:-.02em;
  span{color:${({theme})=>theme.accent};}
`;
const Sub = styled.p`font-size:.73rem;color:${({theme})=>theme.textMuted};margin-top:.2rem;`;
const CloseBtn = styled.button`
  background:none;border:none;color:${({theme})=>theme.textMuted};
  cursor:pointer;font-size:1rem;padding:0;
  &:hover{color:${({theme})=>theme.text};}
`;
const Body = styled.form`padding:1.4rem 1.5rem;display:flex;flex-direction:column;gap:1rem;`;

const Field = styled.div`display:flex;flex-direction:column;gap:.3rem;`;
const Label = styled.label`
  font-size:.67rem;text-transform:uppercase;letter-spacing:.08em;
  color:${({theme})=>theme.textMuted};
`;
const Input = styled.input`
  background:${({theme})=>theme.bg};border:1px solid ${({theme})=>theme.border};
  border-radius:8px;color:${({theme})=>theme.text};
  font-family:${({theme})=>theme.fontBody};font-size:.83rem;
  padding:.58rem .85rem;outline:none;transition:border-color .15s;
  &:focus{border-color:${({theme})=>theme.borderLight};}
  &::placeholder{color:${({theme})=>theme.textMuted};}
`;
const Tarea = styled.textarea`
  background:${({theme})=>theme.bg};border:1px solid ${({theme})=>theme.border};
  border-radius:8px;color:${({theme})=>theme.text};
  font-family:${({theme})=>theme.fontBody};font-size:.83rem;
  padding:.58rem .85rem;outline:none;resize:vertical;min-height:100px;
  transition:border-color .15s;
  &:focus{border-color:${({theme})=>theme.borderLight};}
  &::placeholder{color:${({theme})=>theme.textMuted};}
`;
const Select = styled.select`
  background:${({theme})=>theme.bg};border:1px solid ${({theme})=>theme.border};
  border-radius:8px;color:${({theme})=>theme.text};
  font-family:${({theme})=>theme.fontBody};font-size:.83rem;
  padding:.58rem .85rem;outline:none;cursor:pointer;
  option{background:${({theme})=>theme.bgCard};}
`;
const Row2 = styled.div`display:grid;grid-template-columns:1fr 1fr;gap:.75rem;`;

const CharCount = styled.span`
  font-family:${({theme})=>theme.fontMono};font-size:.62rem;
  color:${({$w,theme})=>$w?theme.red:theme.textMuted};
  text-align:right;margin-top:.1rem;
`;

const Footer = styled.div`
  padding:.85rem 1.5rem;border-top:1px solid ${({theme})=>theme.border};
  display:flex;gap:.6rem;justify-content:flex-end;align-items:center;
`;
const CancelBtn = styled.button`
  padding:.55rem 1rem;border-radius:7px;border:1px solid ${({theme})=>theme.border};
  background:transparent;color:${({theme})=>theme.textDim};
  font-family:${({theme})=>theme.fontBody};font-size:.8rem;font-weight:600;
  cursor:pointer;transition:all .15s;
  &:hover{background:${({theme})=>theme.bgHover};}
`;
const SubmitBtn = styled.button`
  padding:.6rem 1.4rem;border-radius:7px;border:none;
  background:${({theme,disabled})=>disabled?theme.bgHover:theme.accent};
  color:${({theme,disabled})=>disabled?theme.textMuted:"#000"};
  font-family:${({theme})=>theme.fontBody};font-size:.8rem;font-weight:700;
  letter-spacing:.03em;cursor:${({disabled})=>disabled?"not-allowed":"pointer"};
  transition:all .15s;&:hover:not(:disabled){opacity:.88;}
`;

/* Success screen */
const SuccessWrap = styled.div`
  padding:2.5rem 1.5rem;text-align:center;
  display:flex;flex-direction:column;align-items:center;gap:1rem;
`;
const SuccessIcon = styled.div`
  width:60px;height:60px;border-radius:50%;
  background:rgba(79,195,247,.12);border:2px solid ${({theme})=>theme.accent};
  display:flex;align-items:center;justify-content:center;font-size:1.6rem;
`;
const SuccessTitle = styled.h3`
  font-family:${({theme})=>theme.fontDisplay};font-size:1.2rem;font-weight:700;
`;
const SuccessSub = styled.p`
  font-size:.78rem;color:${({theme})=>theme.textMuted};max-width:300px;line-height:1.55;
`;
const TicketRef = styled.div`
  font-family:${({theme})=>theme.fontMono};font-size:.72rem;
  color:${({theme})=>theme.textDim};padding:.4rem .9rem;
  background:${({theme})=>theme.bgHover};border-radius:6px;
  border:1px solid ${({theme})=>theme.border};
`;
const DoneBtn = styled.button`
  padding:.6rem 2rem;border-radius:8px;
  border:1px solid ${({theme})=>theme.borderLight};background:transparent;
  color:${({theme})=>theme.text};font-family:${({theme})=>theme.fontBody};
  font-size:.8rem;font-weight:600;cursor:pointer;transition:all .15s;
  &:hover{background:${({theme})=>theme.bgHover};}
`;

/* Fallback categories — labels resolved at render-time via tm("cat.{value}") */
const CATEGORIES_FALLBACK = [
  { value:"deposit"    },
  { value:"withdrawal" },
  { value:"trade"      },
  { value:"account"    },
  { value:"bug"        },
  { value:"other"      },
];

const MAX = 1000;

export default function ContactModal({ onClose, onSuccess, user }) {
  const { lang } = useI18n();
  const [categories, setCategories] = useState(CATEGORIES_FALLBACK);
  const [subject,    setSubject]    = useState("");
  const [desc,       setDesc]       = useState("");
  const [category,   setCategory]   = useState("other");
  const [priority,   setPriority]   = useState("medium");
  const [loading,    setLoading]    = useState(false);
  const { errors, globalError, setFieldError, setFromApi, clearAll } = useFormErrors();
  const [success,    setSuccess]    = useState(null);

  useEffect(() => {
    getCategories()
      .then(r => setCategories(r.data))
      .catch(() => {});
  }, []);

  // Resolve category label: prefer API-provided label, else fall back to translation
  const renderedCategories = useMemo(
    () => categories.map(c => ({
      value: c.value,
      label: c.label ?? tm(`cat.${c.value}`, lang),
    })),
    [categories, lang]
  );

  const canSubmit = subject.trim().length > 3 && desc.trim().length > 10 && !loading;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    // Validation locale
    clearAll();
    let hasErr = false;
    if (!subject.trim())         { setFieldError("subject", tm("err.subject", lang)); hasErr = true; }
    if (desc.trim().length < 10) { setFieldError("desc",    tm("err.desc",    lang)); hasErr = true; }
    if (hasErr) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await createTicket({ subject, description: desc, category, priority });
      setSuccess(res.data);
      onSuccess?.();
    } catch (err) {
      // Demo mode fallback
      clearAll();
      setSuccess({
        id: `DEMO-${Date.now().toString(36).toUpperCase()}`,
        subject,
        status: "open",
      });
    } finally {
      setLoading(false);
    }
  };

  if (success) return (
    <Overlay onClick={e => e.target === e.currentTarget && onClose()}>
      <Modal>
        <SuccessWrap>
          <SuccessIcon>✓</SuccessIcon>
          <SuccessTitle>{tm("success", lang)}</SuccessTitle>
          <SuccessSub>{tm("successSub", lang)}</SuccessSub>
          <TicketRef>{tm("ref", lang)} {String(success.id).slice(0, 12).toUpperCase()}</TicketRef>
          <DoneBtn onClick={onClose}>{tm("close", lang)}</DoneBtn>
        </SuccessWrap>
      </Modal>
    </Overlay>
  );

  return (
    <Overlay onClick={e => e.target === e.currentTarget && onClose()}>
      <Modal>
        <Head>
          <HeadLeft>
            <Title>{tm("title", lang)} <span>{tm("title2", lang)}</span></Title>
            <Sub>{tm("sub", lang)}</Sub>
          </HeadLeft>
          <CloseBtn onClick={onClose}>✕</CloseBtn>
        </Head>

        <Body onSubmit={handleSubmit}>
          <Field>
            <Label>{tm("subjectL", lang)}</Label>
            <Input
              placeholder={tm("subjectPh", lang)}
              value={subject}
              onChange={e => setSubject(e.target.value)}
              maxLength={255}
              style={errors.subject ? {borderColor:"#ff3b5c"} : {}}
            />
            <FieldError $show={!!errors.subject}>{errors.subject}</FieldError>
          </Field>

          <Row2>
            <Field>
              <Label>{tm("catL", lang)}</Label>
              <Select value={category} onChange={e => setCategory(e.target.value)}>
                {renderedCategories.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </Select>
            </Field>
            <Field>
              <Label>{tm("prioL", lang)}</Label>
              <Select value={priority} onChange={e => setPriority(e.target.value)}>
                <option value="low">{tm("low", lang)}</option>
                <option value="medium">{tm("normal", lang)}</option>
                <option value="high">{tm("high", lang)}</option>
                <option value="urgent">{tm("urgent", lang)}</option>
              </Select>
            </Field>
          </Row2>

          <Field>
            <Label>{tm("descL", lang)}</Label>
            <Tarea
              placeholder={tm("descPh", lang)}
              value={desc}
              onChange={e => setDesc(e.target.value.slice(0, MAX))}
              style={errors.desc ? {borderColor:"#ff3b5c"} : {}}
            />
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <FieldError $show={!!errors.desc}>{errors.desc}</FieldError>
              <CharCount $w={desc.length > MAX * 0.9}>{desc.length}/{MAX}</CharCount>
            </div>
          </Field>

          {/* Info box */}
          <div style={{
            background:"rgba(79,195,247,.06)",border:"1px solid rgba(79,195,247,.2)",
            borderRadius:"8px",padding:".75rem 1rem",fontSize:".72rem",
            color:"#8a9aaa",lineHeight:1.5,display:"flex",gap:".5rem",alignItems:"flex-start"
          }}>
            <span>ℹ</span>
            <span>
              {user ? (
                <>
                  {tm("info.logged.1", lang)} <strong style={{color:"#e8edf2"}}>{user.username}</strong>{tm("info.logged.2", lang)}
                </>
              ) : (
                tm("info.guest", lang)
              )}
            </span>
          </div>

          <ErrorBanner $show={!!globalError}>⚠ {globalError}</ErrorBanner>
        </Body>

        <Footer>
          <CancelBtn onClick={onClose}>{tm("cancel", lang)}</CancelBtn>
          <SubmitBtn disabled={!canSubmit} onClick={handleSubmit}>
            {loading ? tm("sending", lang) : tm("send", lang)}
          </SubmitBtn>
        </Footer>
      </Modal>
    </Overlay>
  );
}
